import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { InteractiveVenueMap } from '../InteractiveVenueMap';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, MapPin, Lock, Unlock, Ban, List } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface VenueSection {
  id: string;
  name: string;
  color: string;
  price: number;
  rows: {
    id: string;
    rowNumber: string;
    seatCount: number;
  }[];
}

interface VenueLayout {
  sections: VenueSection[];
}

interface AdminSeatBlockingDialogProps {
  eventId: string;
  eventTitle: string;
  venueLayout: VenueLayout;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AdminSeatBlockingDialog = ({
  eventId,
  eventTitle,
  venueLayout,
  open,
  onOpenChange,
}: AdminSeatBlockingDialogProps) => {
  const [takenSeats, setTakenSeats] = useState<{
    section: string;
    row_number: string;
    seat_number: string;
    phone?: string;
    email?: string;
  }[]>([]);
  const [selectedSeats, setSelectedSeats] = useState<{
    section: string;
    rowNumber: string;
    seatNumber: string;
  }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && eventId) {
      loadTakenSeats();
    }
  }, [open, eventId]);

  const loadTakenSeats = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select('section, row_number, seat_number, phone, email')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .not('seat_number', 'is', null);

      if (error) throw error;
      setTakenSeats(data || []);
    } catch (error) {
      console.error('Error loading taken seats:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել տեղերը",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get list of admin-blocked seats
  const blockedSeats = useMemo(() => {
    return takenSeats.filter(seat => 
      seat.email?.includes('admin-blocked') || 
      seat.phone?.startsWith('+374-')
    );
  }, [takenSeats]);

  const handleUnblockSeat = async (seat: typeof takenSeats[0]) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('event_id', eventId)
        .eq('section', seat.section)
        .eq('row_number', seat.row_number)
        .eq('seat_number', seat.seat_number)
        .eq('status', 'confirmed')
        .like('email', 'admin-blocked-%@blocked.local');

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Տեղը ազատվեց",
      });

      await loadTakenSeats();
    } catch (error) {
      console.error('Error unblocking seat:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ազատել տեղը",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnblockAllBlocked = async () => {
    if (blockedSeats.length === 0) return;
    
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('event_registrations')
        .delete()
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .like('email', 'admin-blocked-%@blocked.local');

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: `${blockedSeats.length} տեղ ազատվեց`,
      });

      await loadTakenSeats();
    } catch (error) {
      console.error('Error unblocking all seats:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ազատել տեղերը",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSeatSelect = (section: string, rowNumber: string, seatNumber: string) => {
    const seatKey = `${section}-${rowNumber}-${seatNumber}`;
    const isAlreadySelected = selectedSeats.some(
      s => s.section === section && s.rowNumber === rowNumber && s.seatNumber === seatNumber
    );

    if (isAlreadySelected) {
      // Remove from selection
      setSelectedSeats(prev => prev.filter(
        s => !(s.section === section && s.rowNumber === rowNumber && s.seatNumber === seatNumber)
      ));
    } else {
      // Add to selection
      setSelectedSeats(prev => [...prev, { section, rowNumber, seatNumber }]);
    }
  };

  const isSeatBlocked = (section: string, rowNumber: string, seatNumber: string) => {
    return takenSeats.some(
      s => s.section === section && 
           s.row_number === rowNumber && 
           s.seat_number === seatNumber
    );
  };

  const handleBlockSelected = async () => {
    if (selectedSeats.length === 0) return;

    setIsProcessing(true);

    try {
      const seatsToBlock = selectedSeats.filter(
        seat => !isSeatBlocked(seat.section, seat.rowNumber, seat.seatNumber)
      );

      if (seatsToBlock.length === 0) {
        toast({
          title: "Ծանուցում",
          description: "Բոլոր ընտրված տեղերն արդեն արգելափակված են",
        });
        return;
      }

      const registrations = seatsToBlock.map(seat => ({
        event_id: eventId,
        first_name: 'ADMIN',
        last_name: 'BLOCKED',
        email: `admin-blocked-${seat.section}-${seat.rowNumber}-${seat.seatNumber}@blocked.local`,
        phone: `+374-${seat.section}-${seat.rowNumber}-${seat.seatNumber}`,
        section: seat.section,
        row_number: seat.rowNumber,
        seat_number: seat.seatNumber,
        status: 'confirmed',
      }));

      const { error } = await supabase
        .from('event_registrations')
        .insert(registrations);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: `${seatsToBlock.length} տեղ արգելափակվեց`,
      });

      await loadTakenSeats();
      setSelectedSeats([]);
    } catch (error) {
      console.error('Error blocking seats:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց արգելափակել տեղերը",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnblockSelected = async () => {
    if (selectedSeats.length === 0) return;

    setIsProcessing(true);

    try {
      const seatsToUnblock = selectedSeats.filter(
        seat => isSeatBlocked(seat.section, seat.rowNumber, seat.seatNumber)
      );

      if (seatsToUnblock.length === 0) {
        toast({
          title: "Ծանուցում",
          description: "Ընտրված տեղերը արդեն ազատ են",
        });
        return;
      }

      for (const seat of seatsToUnblock) {
        const { error } = await supabase
          .from('event_registrations')
          .delete()
          .eq('event_id', eventId)
          .eq('section', seat.section)
          .eq('row_number', seat.rowNumber)
          .eq('seat_number', seat.seatNumber)
          .eq('status', 'confirmed')
          .like('email', 'admin-blocked-%@blocked.local');

        if (error) throw error;
      }

      toast({
        title: "Հաջողություն",
        description: `${seatsToUnblock.length} տեղ ազատվեց`,
      });

      await loadTakenSeats();
      setSelectedSeats([]);
    } catch (error) {
      console.error('Error unblocking seats:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ազատել տեղերը",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {eventTitle} - Տեղերի կառավարում
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <Tabs defaultValue="map" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="map" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Քարտեզ
                </TabsTrigger>
                <TabsTrigger value="blocked" className="flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Բլոկավորված ({blockedSeats.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="map" className="space-y-4">
                <Alert>
                  <AlertDescription>
                    Սեղմեք տեղերի վրա՝ ընտրելու համար։ Կարող եք ընտրել բազմակի տեղեր։
                    {selectedSeats.length > 0 && (
                      <div className="mt-2 font-medium">
                        Ընտրված է {selectedSeats.length} տեղ
                      </div>
                    )}
                  </AlertDescription>
                </Alert>

                <ScrollArea className="h-[450px] pr-4">
                  <InteractiveVenueMap
                    venueLayout={venueLayout}
                    takenSeats={takenSeats}
                    selectedSeat={selectedSeats}
                    onSeatSelect={handleSeatSelect}
                  />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="blocked" className="space-y-4">
                <div className="flex items-center justify-between">
                  <Alert className="flex-1">
                    <AlertDescription className="flex items-center gap-2">
                      <Ban className="h-4 w-4 text-red-500" />
                      Ադմինի կողմից բլոկավորված տեղեր
                    </AlertDescription>
                  </Alert>
                  {blockedSeats.length > 0 && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleUnblockAllBlocked}
                      disabled={isProcessing}
                      className="ml-2"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Մշակվում է...
                        </>
                      ) : (
                        <>
                          <Unlock className="h-4 w-4 mr-2" />
                          Ազատել բոլորը
                        </>
                      )}
                    </Button>
                  )}
                </div>

                <ScrollArea className="h-[450px] pr-4">
                  {blockedSeats.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Ban className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        Ադմինի կողմից բլոկավորված տեղեր չկան
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {blockedSeats.map((seat, index) => {
                        const sectionInfo = venueLayout.sections.find(
                          s => s.id === seat.section
                        );
                        return (
                          <Card key={index} className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded flex items-center justify-center"
                                style={{ backgroundColor: `${sectionInfo?.color}20` }}
                              >
                                <Ban className="h-5 w-5 text-red-500" />
                              </div>
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  {sectionInfo?.name || seat.section}
                                  <Badge variant="outline" className="text-xs">
                                    Շարք {seat.row_number}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    Տեղ {seat.seat_number}
                                  </Badge>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {sectionInfo?.price?.toLocaleString()} դրամ
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnblockSeat(seat)}
                              disabled={isProcessing}
                            >
                              <Unlock className="h-4 w-4 mr-2" />
                              Ազատել
                            </Button>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between pt-4 border-t gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Փակել
              </Button>
              
              {selectedSeats.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelectedSeats([])}
                    disabled={isProcessing}
                  >
                    Մաքրել ընտրությունը
                  </Button>
                  <Button
                    type="button"
                    onClick={handleUnblockSelected}
                    disabled={isProcessing}
                    variant="destructive"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Մշակվում է...
                      </>
                    ) : (
                      <>
                        <Unlock className="h-4 w-4 mr-2" />
                        Ազատել ({selectedSeats.length})
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleBlockSelected}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Մշակվում է...
                      </>
                    ) : (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Արգելափակել ({selectedSeats.length})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
